import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Lock,
  Shield,
  Upload,
  Download,
  Trash2,
  FileText,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Eye,
  WifiOff,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { cn } from '@/lib/utils';
import { getApiBase } from '../../utils/apiBase';
import { toast } from 'sonner';
import { useOffline } from '../../contexts/OfflineContext';
import {
  VAULT_CATEGORIES,
  WILL_HELPER_TEXT,
  VAULT_DISCLAIMER,
  VAULT_ACCEPTED_HINT,
  isVaultPreviewable,
  formatVaultUploadedDate,
  downloadVaultDocument,
  createVaultPreviewUrl,
} from '../../utils/vaultDocument';

const API = getApiBase();

function categoryIcon(id) {
  if (id === 'photo_document') return ImageIcon;
  return FileText;
}

export function DocumentVault({ treeId, canEdit = false, className }) {
  const { isOnline } = useOffline();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);

  const [form, setForm] = useState({
    vault_category: 'other',
    title: '',
    note: '',
    storage_note: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchDocuments = useCallback(async () => {
    if (!treeId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trees/${treeId}/vault/documents`);
      setDocuments(res.data || []);
    } catch {
      toast.error('Could not load your private documents');
    } finally {
      setLoading(false);
    }
  }, [treeId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    let revoke;
    if (!viewDoc || !isVaultPreviewable(viewDoc)) {
      setPreviewUrl(null);
      return undefined;
    }
    setPreviewLoading(true);
    createVaultPreviewUrl(viewDoc.id, viewDoc.file_mime_type)
      .then((url) => {
        revoke = url;
        setPreviewUrl(url);
      })
      .catch(() => setPreviewUrl(null))
      .finally(() => setPreviewLoading(false));
    return () => {
      if (revoke) window.URL.revokeObjectURL(revoke);
    };
  }, [viewDoc]);

  const resetForm = () => {
    setForm({ vault_category: 'other', title: '', note: '', storage_note: '' });
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFilePick = (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const allowed = ['jpg', 'jpeg', 'jfif', 'png', 'pdf', 'heic', 'heif', 'webp', 'gif', 'avif', 'tif', 'tiff', 'doc', 'docx', 'odt', 'rtf', 'txt'];
    if (!allowed.includes(ext)) {
      toast.error('File type not supported for the private vault');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 15 MB');
      return;
    }
    setSelectedFile(file);
    if (!form.title) {
      setForm((f) => ({ ...f, title: file.name.replace(/\.[^/.]+$/, '') }));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Choose a file to store');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      fd.append('vault_category', form.vault_category);
      fd.append('title', form.title);
      fd.append('note', form.note);
      fd.append('storage_note', form.storage_note);
      await axios.post(`${API}/trees/${treeId}/vault/documents/upload`, fd);
      toast.success('Document stored securely');
      setUploadOpen(false);
      resetForm();
      await fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleReplace = async (file) => {
    if (!replaceTarget || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/documents/${replaceTarget.id}/replace-file`, fd);
      toast.success('Document updated');
      setReplaceTarget(null);
      if (viewDoc?.id === replaceTarget.id) setViewDoc(null);
      await fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not replace file');
    } finally {
      setUploading(false);
      if (replaceInputRef.current) replaceInputRef.current.value = '';
    }
  };

  const handleSaveMeta = async () => {
    if (!viewDoc) return;
    try {
      await axios.put(`${API}/documents/${viewDoc.id}`, {
        title: viewDoc.title,
        description: viewDoc.description,
        vault_category: viewDoc.vault_category,
        storage_note: viewDoc.storage_note,
      });
      toast.success('Details saved');
      await fetchDocuments();
    } catch {
      toast.error('Could not save details');
    }
  };

  const handleDelete = async () => {
    if (!deleteDoc) return;
    try {
      await axios.delete(`${API}/documents/${deleteDoc.id}`);
      toast.success('Document removed');
      setDeleteDoc(null);
      if (viewDoc?.id === deleteDoc.id) setViewDoc(null);
      await fetchDocuments();
    } catch {
      toast.error('Could not delete document');
    }
  };

  return (
    <section
      className={cn(
        'overflow-hidden rounded-2xl border border-[hsl(220_14%_84%)] bg-gradient-to-br from-[hsl(220_20%_99%)] via-[hsl(42_28%_99%)] to-[hsl(38_16%_96%)] shadow-[0_8px_32px_-16px_hsl(220_20%_20%_/_0.1)]',
        className
      )}
      data-testid="document-vault"
      aria-label="Private documents vault"
    >
      <div className="border-b border-[hsl(220_14%_88%)]/80 bg-[hsl(220_24%_98%)]/60 px-5 py-5 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[hsl(220_16%_86%)] bg-white/80 text-[hsl(220_20%_35%)]">
              <Lock className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-[hsl(220_12%_42%)]">
                Private vault
              </p>
              <h2 className="mt-0.5 font-serif text-xl font-normal text-[hsl(24_14%_16%)]">
                Documents vault
              </h2>
              <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-[hsl(32_10%_38%)]">
                A calm, secure place for wills, legal papers, and other private files. Only you can access what you store here.
              </p>
            </div>
          </div>
          {canEdit && isOnline ? (
            <Button
              type="button"
              className="shrink-0 rounded-full"
              onClick={() => setUploadOpen(true)}
              data-testid="vault-upload-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              Store a document
            </Button>
          ) : null}
        </div>
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-[hsl(220_14%_88%)]/70 bg-white/50 px-3 py-2.5 text-xs leading-relaxed text-[hsl(32_10%_40%)]">
          <Shield className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(220_18%_45%)]" aria-hidden />
          <span>{VAULT_DISCLAIMER}</span>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        {!isOnline ? (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-dashed border-[hsl(38_16%_86%)] px-4 py-3 text-sm text-muted-foreground">
            <WifiOff className="h-4 w-4 shrink-0" />
            Reconnect to upload or download vault documents.
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-12" data-testid="vault-loading">
            <Loader2 className="h-7 w-7 animate-spin text-[#8b7355]" aria-label="Loading vault" />
          </div>
        ) : documents.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[hsl(220_14%_86%)] bg-white/40 px-6 py-10 text-center" data-testid="vault-empty">
            <Lock className="mx-auto h-9 w-9 text-[hsl(220_14%_70%)]" aria-hidden />
            <p className="mt-3 font-serif text-lg text-[hsl(24_14%_18%)]">Your vault is empty</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              Upload a will, certificate, or other private document when you are ready. Nothing here is shared with collaborators unless you choose a different archive area.
            </p>
          </div>
        ) : (
          <ul className="space-y-2" role="list">
            {documents.map((doc) => {
              const Icon = categoryIcon(doc.vault_category);
              return (
                <li key={doc.id}>
                  <div
                    className="flex flex-col gap-3 rounded-xl border border-[hsl(220_14%_88%)]/80 bg-white/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                    data-testid={`vault-doc-${doc.id}`}
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(220_20%_96%)] text-[hsl(220_18%_40%)]">
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-serif text-sm text-[hsl(24_14%_16%)]">{doc.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.vault_category_label || doc.vault_category}
                          {' · '}
                          {doc.file_original_filename || doc.file_type?.toUpperCase()}
                          {' · '}
                          Uploaded {formatVaultUploadedDate(doc.created_at)}
                        </p>
                        {doc.description ? (
                          <p className="mt-0.5 line-clamp-1 text-xs text-[hsl(32_10%_42%)]">{doc.description}</p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      {isVaultPreviewable(doc) ? (
                        <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={() => setViewDoc(doc)}>
                          <Eye className="mr-1 h-3.5 w-3.5" />
                          Preview
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-full text-xs"
                        disabled={!isOnline}
                        onClick={() => downloadVaultDocument(doc.id, doc.file_original_filename || doc.title)}
                      >
                        <Download className="mr-1 h-3.5 w-3.5" />
                        Download
                      </Button>
                      {canEdit ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="rounded-full text-xs"
                            disabled={!isOnline || uploading}
                            onClick={() => {
                              setReplaceTarget(doc);
                              replaceInputRef.current?.click();
                            }}
                          >
                            <RefreshCw className="mr-1 h-3.5 w-3.5" />
                            Replace
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="rounded-full text-xs text-destructive hover:text-destructive"
                            onClick={() => setDeleteDoc(doc)}
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" />
                            Delete
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <input
        ref={replaceInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.jfif,.png,.heic,.heif,.webp,.gif,.avif,.tif,.tiff,.doc,.docx,.odt,.rtf,.txt"
        onChange={(e) => handleReplace(e.target.files?.[0])}
      />

      <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-md sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif">Store a private document</DialogTitle>
            <DialogDescription>
              Files stay in your vault — private to you, with secure download only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="vault-category">Category</Label>
              <Select
                value={form.vault_category}
                onValueChange={(v) => setForm((f) => ({ ...f, vault_category: v }))}
              >
                <SelectTrigger id="vault-category" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VAULT_CATEGORIES.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.vault_category === 'will' ? (
              <p className="rounded-lg border border-[hsl(220_14%_88%)] bg-[hsl(220_22%_98%)] px-3 py-2.5 text-xs leading-relaxed text-[hsl(32_10%_38%)]">
                {WILL_HELPER_TEXT}
              </p>
            ) : null}
            <div>
              <Label htmlFor="vault-file">File</Label>
              <Input
                id="vault-file"
                ref={fileInputRef}
                type="file"
                className="mt-1"
                accept=".pdf,.jpg,.jpeg,.jfif,.png,.heic,.heif,.webp,.gif,.avif,.tif,.tiff,.doc,.docx,.odt,.rtf,.txt"
                onChange={(e) => handleFilePick(e.target.files?.[0])}
              />
              <p className="mt-1 text-[11px] text-muted-foreground">{VAULT_ACCEPTED_HINT}</p>
            </div>
            <div>
              <Label htmlFor="vault-title">Display name</Label>
              <Input
                id="vault-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="mt-1"
                placeholder="e.g. Last will and testament (2024)"
              />
            </div>
            <div>
              <Label htmlFor="vault-note">Optional note</Label>
              <Textarea
                id="vault-note"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                className="mt-1"
                rows={2}
                placeholder="Anything helpful for your future self or family"
              />
            </div>
            <div>
              <Label htmlFor="vault-storage-note">Where is the original kept?</Label>
              <Input
                id="vault-storage-note"
                value={form.storage_note}
                onChange={(e) => setForm((f) => ({ ...f, storage_note: e.target.value }))}
                className="mt-1"
                placeholder="e.g. Safe deposit box, attorney's office"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setUploadOpen(false)}>
              Cancel
            </Button>
            <Button type="button" className="rounded-full" disabled={uploading || !selectedFile} onClick={handleUpload}>
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
              Store securely
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(viewDoc)} onOpenChange={(o) => !o && setViewDoc(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">{viewDoc?.title}</DialogTitle>
            <DialogDescription>
              {viewDoc?.vault_category_label} · Uploaded {formatVaultUploadedDate(viewDoc?.created_at)}
            </DialogDescription>
          </DialogHeader>
          {viewDoc && isVaultPreviewable(viewDoc) ? (
            <div className="max-h-[50vh] overflow-auto rounded-lg border bg-muted/20">
              {previewLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : previewUrl && viewDoc.file_mime_type?.startsWith('image/') ? (
                <img src={previewUrl} alt={viewDoc.title} className="mx-auto max-h-[48vh] w-auto" />
              ) : previewUrl && viewDoc.file_type === 'pdf' ? (
                <iframe title={viewDoc.title} src={previewUrl} className="h-[48vh] w-full" />
              ) : (
                <p className="p-6 text-center text-sm text-muted-foreground">Preview unavailable</p>
              )}
            </div>
          ) : null}
          {viewDoc && canEdit ? (
            <div className="space-y-3 border-t pt-4">
              <div>
                <Label>Note</Label>
                <Textarea
                  value={viewDoc.description || ''}
                  onChange={(e) => setViewDoc((d) => ({ ...d, description: e.target.value }))}
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Where original is kept</Label>
                <Input
                  value={viewDoc.storage_note || ''}
                  onChange={(e) => setViewDoc((d) => ({ ...d, storage_note: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={handleSaveMeta}>
                Save details
              </Button>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setViewDoc(null)}>
              Close
            </Button>
            {viewDoc ? (
              <Button
                type="button"
                className="rounded-full"
                disabled={!isOnline}
                onClick={() => downloadVaultDocument(viewDoc.id, viewDoc.file_original_filename || viewDoc.title)}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteDoc)} onOpenChange={(o) => !o && setDeleteDoc(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove from vault?</DialogTitle>
            <DialogDescription>
              This permanently removes &ldquo;{deleteDoc?.title}&rdquo; from your private vault.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setDeleteDoc(null)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" className="rounded-full" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default DocumentVault;
